import React, { useEffect, useRef } from 'react'
import { Box, debounce } from '@mui/material'
import {
  createChart,
  type IChartApi,
  type ISeriesApi,
  type LineData,
  type UTCTimestamp,
  LineStyle,
} from 'lightweight-charts'
import { type PnlV2Point } from './pnl-types'

interface PnlCurveChartProps {
  series: PnlV2Point[]
  height?: number
  /** Whether to show the component decomposition lines. */
  showDecomposition?: boolean
}

function toLineData (series: PnlV2Point[], pick: (p: PnlV2Point) => number): LineData[] {
  return series.map((p) => ({
    time: (Math.floor(new Date(p.ts).getTime() / 1000)) as UTCTimestamp,
    value: pick(p),
  }))
}

// Cumulative interval PnL (transfers excluded). Starts at $0 by contract
// (v2 series[0].period_pnl === 0); the zero line is drawn explicitly so the
// break-even level is always visible (design §7.3).
const PnlCurveChart: React.FC<PnlCurveChartProps> = ({ series, height = 360, showDecomposition = false }) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const chartRef = useRef<IChartApi | null>(null)
  const periodSeriesRef = useRef<ISeriesApi<'Line'> | null>(null)
  const realizedSeriesRef = useRef<ISeriesApi<'Line'> | null>(null)
  const fundingSeriesRef = useRef<ISeriesApi<'Line'> | null>(null)
  const feeSeriesRef = useRef<ISeriesApi<'Line'> | null>(null)
  const unrealSeriesRef = useRef<ISeriesApi<'Line'> | null>(null)

  // Initialize chart once.
  useEffect(() => {
    if (containerRef.current === null) return

    const chart = createChart(containerRef.current, {
      layout: {
        textColor: '#e0e0e0',
        background: { color: '#121212' } as any,
      },
      grid: {
        vertLines: { color: '#2a2a2a' },
        horzLines: { color: '#2a2a2a' },
      },
      crosshair: { mode: 0 },
      rightPriceScale: { autoScale: true, borderColor: '#9d5fce' },
      timeScale: {
        fixLeftEdge: true,
        fixRightEdge: true,
        borderColor: '#9d5fce',
        timeVisible: true,
      },
      localization: {
        locale: 'en-US',
        priceFormatter: (p: number) => `$${p.toFixed(2)}`,
      },
    })
    chartRef.current = chart

    const periodSeries = chart.addLineSeries({
      color: '#4caf50',
      lineWidth: 2,
      title: 'Period PnL',
    })
    periodSeries.createPriceLine({
      price: 0,
      color: '#9e9e9e',
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      axisLabelVisible: true,
      title: '',
    })
    periodSeriesRef.current = periodSeries

    realizedSeriesRef.current = chart.addLineSeries({
      color: '#64b5f6',
      lineWidth: 1,
      lineStyle: LineStyle.Dotted,
      title: 'Realized',
      visible: showDecomposition,
    })
    fundingSeriesRef.current = chart.addLineSeries({
      color: '#ba68c8',
      lineWidth: 1,
      lineStyle: LineStyle.Dotted,
      title: 'Funding',
      visible: showDecomposition,
    })
    feeSeriesRef.current = chart.addLineSeries({
      color: '#ef5350',
      lineWidth: 1,
      lineStyle: LineStyle.Dotted,
      title: 'Fees+Rebate',
      visible: showDecomposition,
    })
    unrealSeriesRef.current = chart.addLineSeries({
      color: '#ffb74d',
      lineWidth: 1,
      lineStyle: LineStyle.Dotted,
      title: 'Unrealized Δ',
      visible: showDecomposition,
    })

    const ro = new ResizeObserver(debounce((entries: Array<{ contentRect: any }>) => {
      const rect = entries[0]?.contentRect
      if (rect !== undefined && chartRef.current !== null) {
        chartRef.current.resize(rect.width, rect.height)
        chartRef.current.timeScale().fitContent()
      }
    }, 50))
    ro.observe(containerRef.current)

    return () => {
      ro.disconnect()
      chart.remove()
      chartRef.current = null
    }
  }, [])

  // Toggle decomposition series visibility when prop changes.
  useEffect(() => {
    realizedSeriesRef.current?.applyOptions({ visible: showDecomposition })
    fundingSeriesRef.current?.applyOptions({ visible: showDecomposition })
    feeSeriesRef.current?.applyOptions({ visible: showDecomposition })
    unrealSeriesRef.current?.applyOptions({ visible: showDecomposition })
  }, [showDecomposition])

  // Update data whenever series prop changes.
  useEffect(() => {
    if (chartRef.current === null) return
    periodSeriesRef.current?.setData(toLineData(series, (p) => p.period_pnl))
    realizedSeriesRef.current?.setData(toLineData(series, (p) => p.realized_cum))
    fundingSeriesRef.current?.setData(toLineData(series, (p) => p.funding_cum))
    feeSeriesRef.current?.setData(toLineData(series, (p) => p.fee_cum + p.rebate_cum))
    unrealSeriesRef.current?.setData(toLineData(series, (p) => p.unrealized_change))
    chartRef.current.timeScale().fitContent()
  }, [series])

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        height,
        backgroundColor: '#121212',
        borderRadius: 1,
      }}
    />
  )
}

export default PnlCurveChart
